import { Box, Typography } from "@mui/material";
import { motion } from "motion/react";
import { palette } from "../theme";

interface JournalTimelineProps {
  logs: string[];
}

// `serverStatus.addLog` prefixes the line with `HH:MM:SS:`. Split that off so we
// can render the timestamp in mono and the message in the body face.
const SPLIT = /^(\d{2}:\d{2}:\d{2}):\s*(.*)$/;

function splitEntry(line: string): { time: string | null; message: string } {
  const m = SPLIT.exec(line);
  if (!m) return { time: null, message: line };
  return { time: m[1], message: m[2] };
}

export function JournalTimeline({ logs }: JournalTimelineProps): JSX.Element {
  if (logs.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="body2" sx={{ color: palette.inkSoft }}>
          Chưa có sự kiện nào.
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ position: "relative", pl: 3 }}>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: "9px",
          top: 0,
          bottom: 0,
          width: 1,
          backgroundColor: palette.border,
        }}
      />
      {logs.slice().reverse().map((entry, i) => {
        const { time, message } = splitEntry(entry);
        return (
          <motion.div
            key={`${i}-${entry}`}
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.18, delay: Math.min(i * 0.015, 0.2) }}
            style={{
              position: "relative",
              paddingBottom: 22,
            }}
          >
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                left: -19,
                top: 6,
                width: 9,
                height: 9,
                borderRadius: "50%",
                backgroundColor: palette.cream,
                border: `1.5px solid ${palette.ink}`,
              }}
            />
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1.5 }}>
              {time && (
                <Box
                  component="span"
                  sx={{
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: "0.72rem",
                    color: palette.inkSoft,
                    letterSpacing: "0.04em",
                    fontVariantNumeric: "tabular-nums",
                    flexShrink: 0,
                  }}
                >
                  {time}
                </Box>
              )}
              <Typography
                sx={{
                  fontFamily: '"Geist", system-ui, sans-serif',
                  fontSize: "0.92rem",
                  color: palette.ink,
                  lineHeight: 1.45,
                }}
              >
                {message}
              </Typography>
            </Box>
          </motion.div>
        );
      })}
    </Box>
  );
}
